import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PrototypeDto } from '@aiai/shared';
import { setPrototypeResourceLimits } from '../api';
import { invalidatePrototype } from '../api/prototypeCache';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

const MEMORY_MIN = 64;
const MEMORY_MAX = 1024;
const CPU_MIN = 0.25;
const CPU_MAX = 2.0;

/**
 * Per-prototype memory and CPU resource limit overrides.
 * Null values fall back to the global PROTOTYPE_SERVE_MEMORY_MB /
 * PROTOTYPE_SERVE_CPU_LIMIT configuration. Changes trigger a full
 * redeploy so Docker picks up the new HostConfig limits.
 */
export function PrototypeResourceLimitsCard({ prototype }: { prototype: PrototypeDto }) {
  const queryClient = useQueryClient();
  const current = prototype.deployment;

  const [memoryEnabled, setMemoryEnabled] = useState(current.memoryMb != null);
  const [memoryMb, setMemoryMb] = useState(current.memoryMb ?? 256);
  const [cpuEnabled, setCpuEnabled] = useState(current.cpuLimit != null);
  const [cpuLimit, setCpuLimit] = useState(current.cpuLimit ?? 0.5);

  // Sync local state when the prototype reloads (e.g. after a save).
  useEffect(() => {
    setMemoryEnabled(current.memoryMb != null);
    if (current.memoryMb != null) setMemoryMb(current.memoryMb);
    setCpuEnabled(current.cpuLimit != null);
    if (current.cpuLimit != null) setCpuLimit(current.cpuLimit);
  }, [current.memoryMb, current.cpuLimit]);

  const saveMutation = useMutation({
    mutationFn: () =>
      setPrototypeResourceLimits(prototype.id, {
        memoryMb: memoryEnabled ? memoryMb : null,
        cpuLimit: cpuEnabled ? cpuLimit : null,
      }),
    onSuccess: () => {
      invalidatePrototype(queryClient, prototype.id);
    },
  });

  const memoryValid = !memoryEnabled || (memoryMb >= MEMORY_MIN && memoryMb <= MEMORY_MAX);
  const cpuValid = !cpuEnabled || (cpuLimit >= CPU_MIN && cpuLimit <= CPU_MAX);
  const isDirty =
    memoryEnabled !== (current.memoryMb != null) ||
    (memoryEnabled && memoryMb !== current.memoryMb) ||
    cpuEnabled !== (current.cpuLimit != null) ||
    (cpuEnabled && cpuLimit !== current.cpuLimit);

  return (
    <SectionCard title="Resource limits">
      <div className="space-y-4">
        <p className="text-xs text-ink-muted">
          Override the global memory and CPU caps for this prototype.
          Changes trigger a redeploy so the new limits take effect in
          Docker&apos;s HostConfig.
        </p>

        {/* Memory */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <input
              id="resource-memory-enabled"
              type="checkbox"
              className="checkbox"
              checked={memoryEnabled}
              onChange={(e) => setMemoryEnabled(e.target.checked)}
            />
            <label htmlFor="resource-memory-enabled" className="text-sm text-ink-muted">
              Override memory limit
            </label>
          </div>
          {memoryEnabled && (
            <div className="ml-6 flex items-center gap-2">
              <input
                type="number"
                className="input w-28"
                min={MEMORY_MIN}
                max={MEMORY_MAX}
                step={64}
                value={memoryMb}
                onChange={(e) => setMemoryMb(Number(e.target.value))}
              />
              <span className="text-xs text-ink-muted">MiB ({MEMORY_MIN}–{MEMORY_MAX})</span>
              {!memoryValid && (
                <span className="text-xs text-danger">
                  Must be {MEMORY_MIN}–{MEMORY_MAX} MiB
                </span>
              )}
            </div>
          )}
        </div>

        {/* CPU */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <input
              id="resource-cpu-enabled"
              type="checkbox"
              className="checkbox"
              checked={cpuEnabled}
              onChange={(e) => setCpuEnabled(e.target.checked)}
            />
            <label htmlFor="resource-cpu-enabled" className="text-sm text-ink-muted">
              Override CPU limit
            </label>
          </div>
          {cpuEnabled && (
            <div className="ml-6 flex items-center gap-2">
              <input
                type="number"
                className="input w-28"
                min={CPU_MIN}
                max={CPU_MAX}
                step={0.25}
                value={cpuLimit}
                onChange={(e) => setCpuLimit(Number(e.target.value))}
              />
              <span className="text-xs text-ink-muted">vCPU ({CPU_MIN}–{CPU_MAX})</span>
              {!cpuValid && (
                <span className="text-xs text-danger">
                  Must be {CPU_MIN}–{CPU_MAX} vCPU
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={!isDirty || !memoryValid || !cpuValid || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
            Save &amp; redeploy
          </button>
        </div>

        {saveMutation.isError && (
          <Callout tone="danger">
            {errorMessage(saveMutation.error, 'Could not update resource limits')}
          </Callout>
        )}

        {saveMutation.isSuccess && (
          <Callout tone="info">Resource limits saved — redeploy queued.</Callout>
        )}
      </div>
    </SectionCard>
  );
}
